import { useCallback, useEffect, useMemo, useState } from 'react';
import { BASE_PRECISION_EXP, BigNum, SpotMarketConfig } from '@drift-labs/sdk';
import { COMMON_UI_UTILS, IF_STAKE_ACCOUNT_BASE_RENT } from '@drift/common';
import { useAddInsuranceFundStake } from 'src/hooks/driftActions/insuranceFund/useAddInsuranceFundStake';
import useStakingUtils from 'src/components/Staking/hooks/useStakingUtils';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { useShallow } from 'zustand/react/shallow';
import useTransferableWalletCollateralBalance from 'src/hooks/useTransferableWalletCollateralBalance';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import useSubaccountCollateralBalanceForBank from 'src/hooks/useSubaccountCollateralBalanceForBank';
import useCurrentWalletCollateralBalance from 'src/hooks/useCurrentWalletCollateralBalance';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useAccountsBeingLiquidated from 'src/hooks/Liquidations/useAccountsBeingLiquidated';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useTriggerRefreshIfStakeData } from 'src/hooks/query/ifData/useAllIfData';
import useWalletIdentity from 'src/hooks/wallets/useWalletIdentity';
import useModal from 'src/components/Modals/hooks/useModal';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

interface Params {
	isDriftStaking: boolean | undefined;
	defaultSelectedMarket: SpotMarketConfig;
	onConfirm?: () => void;
}

export default function useStakeForm({
	isDriftStaking,
	defaultSelectedMarket,
	onConfirm,
}: Params) {
	const { show } = useModal();
	const walletAdapter = useCurrentWalletAdapter();
	const [currentAccountId, currentAccountKey] = useDriftAccountsStore(
		useShallow((s) => [
			s.accounts[s.currentUserKey ?? '']?.userId,
			s.accounts[s.currentUserKey ?? '']?.userKey,
		])
	);

	const [subAccountIdToStakeFrom, setSubAccountIdToStakeFrom] =
		useState(currentAccountId);
	const [subAccountKeyToStakeFrom, setSubAccountKeyToStakeFrom] =
		useState(currentAccountKey);

	const [isStakingFromSubaccount, setIsStakingFromSubaccount] = useState(false);

	const [hasAcceptedRisks, setHasAcceptedRisks] = useState(false);

	const {
		selectedMarket,
		selectedMarketStakeData,
		amount,
		setAmount,
		amountBignum,
		isWalletConnected,
		userStakeLoaded,
		userNeedsStakeAccount,
		excludedMarkets,
		unstakingPeriodAsDays,
		userStakeAccountBalance,
		hasExistingUnstakeRequest,
		existingUnstakeRequestAmount,
		existingUnstakeRequestTimestamp,
		canWithdraw,
		handleCancelUnstakeRequest,
		handleWithdrawUnstakedAssets,
		handleSelectedMarketChange,
		displayPrecision,
		captureEvent,
		isGeoblocked,
		isSubmitting,
		setIsSubmitting,
	} = useStakingUtils({
		isDriftStaking,
		defaultSelectedMarket,
		onConfirm,
	});

	const { isPrivyNativeAuth } = usePrivyWalletContext();

	const { addInsuranceFundStake } = useAddInsuranceFundStake();

	const [walletBalance] = useCurrentWalletCollateralBalance(selectedMarket);

	const [transferableWalletBalance] =
		useTransferableWalletCollateralBalance(selectedMarket);

	const targetCollatBalances = useAccountTargetSpotBalance(
		selectedMarket,
		subAccountKeyToStakeFrom,
		true
	);

	const subAccountBalance = useSubaccountCollateralBalanceForBank({
		accountKey: subAccountKeyToStakeFrom ?? '',
		market: selectedMarket,
	});

	const withdrawalLimit = targetCollatBalances.withdrawLimitBase.shiftTo(
		selectedMarket.precisionExp
	);

	const { currentCollatBalanceBigNum, depositableBalanceBigNum } = (() => {
		if (!isWalletConnected) {
			return {
				currentCollatBalanceBigNum: BigNum.zero(selectedMarket.precisionExp),
				depositableBalanceBigNum: BigNum.zero(selectedMarket.precisionExp),
			};
		}

		if (isStakingFromSubaccount) {
			return {
				currentCollatBalanceBigNum: subAccountBalance,
				depositableBalanceBigNum: withdrawalLimit,
			};
		}

		return {
			currentCollatBalanceBigNum: walletBalance,
			depositableBalanceBigNum: transferableWalletBalance,
		};
	})();

	const exceededMax = amountBignum.gt(depositableBalanceBigNum);

	useEffect(() => {
		if (exceededMax) {
			setAmount(depositableBalanceBigNum.print());
		}
	}, [exceededMax, depositableBalanceBigNum]);

	const handleHasAcceptedRisksChange = useCallback(() => {
		setHasAcceptedRisks((curr) => !curr);
	}, []);

	const handleStakeFromSourceChange = useCallback(
		(value: string) => {
			if (value === 'wallet') {
				setIsStakingFromSubaccount(false);
			} else if (walletAdapter.publicKey) {
				const newValue = Number(value);
				setIsStakingFromSubaccount(true);
				setSubAccountIdToStakeFrom(newValue);
				setSubAccountKeyToStakeFrom(
					COMMON_UI_UTILS.getUserKey(Number(newValue), walletAdapter.publicKey)
				);
			}
		},
		[walletAdapter.publicKey]
	);

	const refreshIfStakeData = useTriggerRefreshIfStakeData();

	const handleSubmit = useCallback(async () => {
		if (!isWalletConnected) {
			show({ type: 'connect_wallet', props: {} });
			return;
		}

		setIsSubmitting(true);

		captureEvent('submitted_add_if_stake', {
			spot_market_symbol: selectedMarket.symbol,
		});

		const result = await addInsuranceFundStake({
			amount: amountBignum.val,
			marketIndex: selectedMarket.marketIndex,
			initializeStakeAccount: userNeedsStakeAccount,
			fromSubaccount: isStakingFromSubaccount ? true : undefined,
			userId: isStakingFromSubaccount ? subAccountIdToStakeFrom : undefined,
			userKey: isStakingFromSubaccount ? subAccountKeyToStakeFrom : undefined,
		});

		setIsSubmitting(false);

		if (result) {
			onConfirm?.();
			setTimeout(() => refreshIfStakeData(), 1000);
		}
	}, [
		isWalletConnected,
		addInsuranceFundStake,
		selectedMarket,
		captureEvent,
		refreshIfStakeData,
		amountBignum,
		userNeedsStakeAccount,
		isStakingFromSubaccount,
		subAccountIdToStakeFrom,
		subAccountKeyToStakeFrom,
		show,
	]);

	const submitButtonContent = isWalletConnected ? 'Stake' : 'Connect Wallet';

	const identity = useWalletIdentity();
	const availableSubAccounts = useLazySubAccounts();
	const stakeFromOptions = useMemo(() => {
		if (!isWalletConnected) {
			return [{ label: 'Wallet (Not connected)', value: 'wallet' }];
		}

		const label = walletAdapter.name
			? `${walletAdapter.name} Wallet (${identity})`
			: identity;

		return [
			{ label, value: 'wallet' },
			...availableSubAccounts
				.filter(
					(account) =>
						!account.isDelegatedTo &&
						account.client?.getUserAccount().poolId === selectedMarket.poolId
				)
				.map((account) => ({
					label: account.name,
					value: `${account.userId}`,
				})),
		];
	}, [availableSubAccounts, selectedMarket, walletAdapter, isWalletConnected]);

	const stakeFromSelect = useSelect({
		options: stakeFromOptions,
		selectedValue: isStakingFromSubaccount
			? `${subAccountIdToStakeFrom}`
			: stakeFromOptions[0].value,
		onSelectedValueChange: handleStakeFromSourceChange,
	});

	const isMaxSolDeposit =
		amount !== '' &&
		amountBignum.eq(depositableBalanceBigNum) &&
		selectedMarket.symbol === 'SOL';

	const hasAcceptedTerms = !userNeedsStakeAccount || hasAcceptedRisks;

	const isStakeDisabled = selectedMarketStakeData?.pausedOperations.includes(
		COMMON_UI_UTILS.InsuranceFundOperationsMap.ADD
	);

	const accountsBeingLiquidated = useAccountsBeingLiquidated();
	const selectedAccountCanBeLiqd = accountsBeingLiquidated.find(
		(account) => `${account.userKey}` === `${subAccountKeyToStakeFrom}`
	);

	const solBalance = useDriftStore((s) => s.wallet.currentSolBalance);
	const isInsufficientSolBalance = solBalance.lt(IF_STAKE_ACCOUNT_BASE_RENT);

	const allowStake =
		// Disable stake when Privy-native for now.
		!isPrivyNativeAuth &&
		userStakeLoaded &&
		hasAcceptedTerms &&
		!isStakeDisabled &&
		!hasExistingUnstakeRequest &&
		!isGeoblocked &&
		!exceededMax &&
		!isSubmitting &&
		!!amount &&
		amountBignum.gt(BigNum.zero(BASE_PRECISION_EXP), true) &&
		!(userNeedsStakeAccount && isInsufficientSolBalance) &&
		!(subAccountKeyToStakeFrom && selectedAccountCanBeLiqd);

	const isSubmitDisabled = isWalletConnected && !allowStake;

	return {
		selectedMarket,
		amount,
		setAmount,
		amountBignum,
		currentCollatBalanceBigNum,
		depositableBalanceBigNum,
		targetCollatBalances,
		userStakeAccountBalance,
		excludedMarkets,
		hasExistingUnstakeRequest,
		existingUnstakeRequestAmount,
		existingUnstakeRequestTimestamp,
		canWithdraw,
		withdrawalLimit,
		isWalletConnected,
		isSubmitDisabled,
		isSubmitting,
		isStakingFromSubaccount,
		stakeFromSelect,
		subAccountIdToStakeFrom,
		subAccountKeyToStakeFrom,
		displayPrecision,
		isMaxSolDeposit,
		isInsufficientSolBalance,
		selectedAccountCanBeLiqd,
		isGeoblocked,
		userNeedsStakeAccount,
		unstakingPeriodAsDays,
		submitButtonContent,
		hasAcceptedRisks,
		handleHasAcceptedRisksChange,
		handleStakeFromSourceChange,
		handleSelectedMarketChange,
		handleCancelUnstakeRequest,
		handleWithdrawUnstakedAssets,
		handleSubmit,
	};
}
